import webstomp, { Client } from "webstomp-client";
import { apiAuthUrl } from "./apiUrl";
import { rest } from "./rest";

const makeWSUrl = () =>
  `${window.location.origin.replace(/^http/, "ws")}/gateway-service/services/push-service/broadcast`;

const reconnectDelayMin = 1000;
const reconnectDelayMax = 30000;

export type StompStatus = "connecting" | "none" | "ready";

export type StompConnectionOptions = {
  /** Подписки. Вызывается после каждого CONNECT: и первого, и после переподключения */
  subscribe: (client: Client) => void;
  /**
   * Дочитать с сервера то, что могло прийти по сокету, пока его не было:
   * broadcast не переигрывается, пропущенное сообщение потеряно навсегда.
   * Вызывается после каждого CONNECT и при возврате на вкладку.
   */
  resync: () => Promise<void>;
  onStatus?: (status: StompStatus) => void;
  onError: (error: unknown) => void;
};

export type StompConnection = {
  stop: () => void;
};

/**
 * STOMP-соединение с push-service, которое само восстанавливается.
 *
 * Переподключаемся при любом закрытии, кроме stop(): код закрытия зависит
 * от того, кто рвёт соединение (push-service, gateway, сам webstomp по
 * таймауту heartbeat), и угадывать его бесполезно.
 */
export const startStompConnection = ({
  subscribe,
  resync,
  onStatus,
  onError,
}: StompConnectionOptions): StompConnection => {
  let client: Client | null = null;
  let stopped = false;
  let attempt = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const open = () => {
    const current = webstomp.client(makeWSUrl(), {
      debug: false,
      // push-service рвёт сессию ("Session closed."), если 30с не слышит
      // heartbeat клиента. В фоновой вкладке Chrome троттлит setInterval
      // до раза в минуту, и пингер webstomp не успевает. Поэтому клиент
      // heartbeat не шлёт, а только слушает серверный: мёртвое соединение
      // заметит проверка входящих heartbeat в webstomp и закроет сокет.
      heartbeat: { incoming: 10000, outgoing: 0 },
      // Исключаем v10.stomp: в STOMP 1.0 webstomp не следит за heartbeat.
      protocols: ["v12.stomp", "v11.stomp"],
    });
    client = current;
    onStatus?.("connecting");

    current.connect(
      {},
      () => {
        if (stopped || current !== client) return;
        attempt = 0;
        onStatus?.("ready");
        subscribe(current);
        resync().catch(onError);
      },
      (error) => {
        // ERROR-кадр сервер шлёт перед закрытием сокета, переподключаемся
        // по самому закрытию
        if (!(error instanceof CloseEvent)) return;
        if (stopped || current !== client) return;
        client = null;
        onStatus?.("none");
        scheduleReconnect();
      },
    );
  };

  const reconnect = async () => {
    timer = undefined;
    // Gateway проверяет jwt_token уже на handshake. Пока вкладка простаивает,
    // REST-запросов нет и токен никто не обновляет, а с протухшим токеном
    // handshake получает 401. rest обновит токен, если он протух.
    // Ошибку не показываем: пока сервер недоступен, она ожидаема.
    await rest.get(apiAuthUrl("/current")).catch(() => undefined);
    if (!stopped && !client) open();
  };

  const scheduleReconnect = () => {
    const delay = Math.min(reconnectDelayMin * 2 ** attempt, reconnectDelayMax);
    attempt += 1;
    timer = setTimeout(reconnect, delay);
  };

  const onVisibilityChange = () => {
    if (stopped || document.visibilityState !== "visible") return;
    if (client?.connected) {
      // Сокет мог умереть так, что webstomp ещё не заметил,
      // поэтому пропущенное дочитываем и при живом соединении
      resync().catch(onError);
    } else if (timer !== undefined) {
      // Пользователь вернулся, ждать конца backoff незачем
      clearTimeout(timer);
      attempt = 0;
      reconnect();
    }
  };

  document.addEventListener("visibilitychange", onVisibilityChange);
  open();

  return {
    stop() {
      stopped = true;
      clearTimeout(timer);
      timer = undefined;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      const current = client;
      client = null;
      if (current?.connected) {
        current.disconnect();
      } else {
        current?.ws?.close();
      }
    },
  };
};
